// Browser-side client for the /api/* proxy. The stored key travels in the
// X-Demo-Key header; the proxy re-attaches it upstream as x-api-key.

import type { CreateBody } from "./validation";

export interface Alert {
  id: string;
  status: "ARMED" | "FIRED";
  direction: "ABOVE" | "BELOW";
  targetPrice: number;
  referencePrice: number;
  pct: number | null;
  createdAt: string;
  firedAt: string | null;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function call(path: string, key: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("X-Demo-Key", key);
  let resp: Response;
  try {
    resp = await fetch(`/api${path}`, { ...init, headers });
  } catch {
    throw new ApiError(0, "Network error");
  }
  if (!resp.ok) {
    let apiMessage = "";
    try {
      const data = (await resp.clone().json()) as { error?: string };
      apiMessage = data.error ?? "";
    } catch {
      // non-JSON error body; leave apiMessage empty
    }
    throw new ApiError(resp.status, apiMessage);
  }
  return resp;
}

/** Read the tenant's notification email; `null` when not set yet. */
export async function getEmail(key: string): Promise<string | null> {
  const resp = await call("/email", key);
  const data = (await resp.json()) as { email: string | null };
  return data.email;
}

/** Set the tenant's notification email; returns the saved value. */
export async function setEmail(key: string, email: string): Promise<string> {
  const resp = await call("/email", key, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const data = (await resp.json()) as { email: string };
  return data.email;
}

export async function listAlerts(key: string): Promise<Alert[]> {
  const resp = await call("/alerts", key);
  return (await resp.json()) as Alert[];
}

export async function createAlert(key: string, body: CreateBody): Promise<Alert> {
  const resp = await call("/alerts", key, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  return (await resp.json()) as Alert;
}

export async function rearmAlert(key: string, id: string): Promise<void> {
  await call(`/alerts/${encodeURIComponent(id)}/rearm`, key, { method: "POST" });
}

export async function deleteAlert(key: string, id: string): Promise<void> {
  await call(`/alerts/${encodeURIComponent(id)}`, key, { method: "DELETE" });
}

/** Map an ApiError to a user-facing message (per the design's error handling). */
export function friendlyError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.status) {
      case 0:
        return "Something went wrong — check your connection and retry.";
      case 403:
        return "Invalid or missing key — ask for a new link.";
      case 503:
        return "Price not available yet, try again in a minute.";
      case 409:
        return "Set your notification email first.";
      case 400:
        return err.message || "Invalid request.";
      default:
        return err.message || "Something went wrong.";
    }
  }
  return "Something went wrong.";
}
